import React, {
  createContext,
  useState,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import { UserResponse } from '../common/interfaces/userResponse';
import type { BoomiModel } from '@components/ModelsManagement/types/models.types';

type BoomiAgentFoundationContextType = {
  selectedAgent: any;
  setSelectedAgent: any;
  isLeftNavCollapsed: boolean;
  setIsLeftNavCollapsed: any;
  isFloatingPanelPinned: boolean;
  setIsFloatingPanelPinned: any;
  builderDialogTitle: string;
  setBuilderDialogTitle: any;
  customAgentChatSessionId: string;
  setCustomAgentChatSessionId: React.Dispatch<React.SetStateAction<string>>;
  showBuilderDialog: boolean;
  setShowBuilderDialog: React.Dispatch<React.SetStateAction<boolean>>;
  showPreview: boolean;
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>;
  isConversationInProgress: boolean;
  setIsConversationInProgress: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomAgentConversationPage: boolean;
  setShowCustomAgentConversationPage: React.Dispatch<React.SetStateAction<boolean>>;
  initialCustomAgentUserPrompt: string;
  setInitialCustomAgentUserPrompt: any;
  initialCustomAgentUserFiles: File[];
  setInitialCustomAgentUserFiles: any;
  showAddNewTaskDialog: boolean;
  setShowAddNewTaskDialog: React.Dispatch<React.SetStateAction<boolean>>;
  showDialogIcon: boolean;
  setDialogIcon: React.Dispatch<React.SetStateAction<boolean>>;
  testMessages: any;
  setTestMessages: any;
  customAgentChatMessages: any;
  setCustomAgentChatMessages: any;
  addNewTaskDialogTitle: string;
  setAddNewTaskDialogTitle: any;
  isMobileView: boolean;
  setIsMobileView: any;
  showAIGeneratedInstructions: boolean;
  setShowAIGeneratedInstructions: React.Dispatch<React.SetStateAction<boolean>>;
  showToolsSelectionTable: boolean;
  setShowToolsSelectionTable: React.Dispatch<React.SetStateAction<boolean>>;
  wizardData: any;
  updateWizardStep: any;
  resetWizardData: any;
  completeStep: any;
  clearStepData: any;
  setWizardData: any;
  toolWizardData: any;
  setToolWizardData: any;
  clearToolWizardData: any,
  resetToolWizardData: any,
  getDefaultToolWizardData: any,
  updateToolWizardData: any,
  updateToolBasicInfo: any;
  addInputParameter: any;
  addExample: any;
  addDynamicProcess: any;
  handleDynamicProcessInputChange: any;
  deleteDynamicProcess: any,
  updateSelectedAgent: any;
  addFilterDatas: any;
  updateApiConfigurationField: any;
  handleFilterInputChange: any,
  deleteFilters: any,
  isReffererTool: any,
  setIsReffererTool: any,
  cachedSourceDetails: any,
  setCachedSourceDetails: React.Dispatch<React.SetStateAction<any>>,
  cachedBulkToolsData: { agentId: string; data: any[] } | null,
  setCachedBulkToolsData:
  React.Dispatch<React.SetStateAction<{ agentId: string; data: any[] } | null>>,
  cachedBulkKnowledgeBasesData: { agentId: string; data: any[] } | null,
  setCachedBulkKnowledgeBasesData:
  React.Dispatch<React.SetStateAction<{ agentId: string; data: any[] } | null>>,
  cachedBulkGlossariesData: { agentId: string; data: Record<string, any> } | null,
  setCachedBulkGlossariesData:
  React.Dispatch<React.SetStateAction<{ agentId: string; data: Record<string, any> } | null>>,
  userPrivileges: any,
  setUserPrivileges: any,
  userResponse: any,
  setUserResponse: any,
  createAgentMethod: string,
  setCreateAgentMethod: any,
  currentView: string,
  setCurrentView: any,
  integrationRecommendationIcon: boolean,
  setIntegrationRecommendationIcon: any,
  abortController: AbortController | null;
  createAbortController: () => AbortController;
  abortRequests: () => void;
  savedFileInferenceConfig: any;
  setSavedFileInferenceConfig: React.Dispatch<React.SetStateAction<any>>;
  savedConversationStarters: any;
  setSavedConversationStarters: React.Dispatch<React.SetStateAction<any>>;
  boomiModels: BoomiModel[];
  setBoomiModels: React.Dispatch<React.SetStateAction<BoomiModel[]>>;
};

const BoomiAgentFoundationContext = createContext<
BoomiAgentFoundationContextType | undefined
>(undefined);

function BoomiAgentFoundationContextProvider(props: { children: any; userResponse: any }) {
  const { children, userResponse: initialUserResponse = null } = props;
  const [selectedAgent, setSelectedAgent] = useState<any>({});
  const [builderDialogTitle, setBuilderDialogTitle] = useState<string>('');
  const [customAgentChatSessionId, setCustomAgentChatSessionId] = useState<string>('');
  const [showBuilderDialog, setShowBuilderDialog] = useState(false);
  const [isLeftNavCollapsed, setIsLeftNavCollapsed] = useState(true);
  const [isFloatingPanelPinned, setIsFloatingPanelPinned] = useState(false);
  const [showAddNewTaskDialog, setShowAddNewTaskDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isConversationInProgress, setIsConversationInProgress] = useState(false);
  const [showCustomAgentConversationPage, setShowCustomAgentConversationPage] = useState(false);
  const [showDialogIcon, setDialogIcon] = useState(false);
  const [addNewTaskDialogTitle, setAddNewTaskDialogTitle] = useState<string>('');
  const [initialCustomAgentUserPrompt, setInitialCustomAgentUserPrompt] = useState<string>('');
  const [initialCustomAgentUserFiles, setInitialCustomAgentUserFiles] = useState<File[]>([]);
  const [showAIGeneratedInstructions, setShowAIGeneratedInstructions] = useState(false);
  const [showToolsSelectionTable, setShowToolsSelectionTable] = useState(false);
  const [isReffererTool, setIsReffererTool] = useState(false);
  const [cachedSourceDetails, setCachedSourceDetails] = useState<any>(null);
  const [cachedBulkToolsData,
    setCachedBulkToolsData] = useState<{ agentId: string; data: any[] } | null>(null);
  const [cachedBulkKnowledgeBasesData,
    setCachedBulkKnowledgeBasesData] = useState<{ agentId: string; data: any[] } | null>(null);
  const [cachedBulkGlossariesData,
    setCachedBulkGlossariesData] = useState<{
    agentId: string;
    data: Record<string, any>;
  } | null>(null);
  const [userPrivileges, setUserPrivileges] = useState<UserResponse | null>(null);
  const [userResponse, setUserResponse] = useState<UserResponse | null>(null);
  const [testMessages, setTestMessages] = useState<any[]>([]);
  const [customAgentChatMessages, setCustomAgentChatMessages] = useState<any[]>([]);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const [createAgentMethod, setCreateAgentMethod] = useState<string>('');
  const [currentView, setCurrentView] = useState<string>('table');
  const [integrationRecommendationIcon,
    setIntegrationRecommendationIcon] = useState<boolean>(false);
  const [savedFileInferenceConfig, setSavedFileInferenceConfig] = useState<any>(null);
  const [savedConversationStarters, setSavedConversationStarters] = useState<any>(null);
  const [boomiModels, setBoomiModels] = useState<BoomiModel[]>([]);

  useEffect(() => {
    if (initialUserResponse) {
      setUserResponse(initialUserResponse);
    }
  }, [initialUserResponse]);

  useEffect(() => {
    const handleWindowResize = () => {
      if (window.innerWidth <= 1200) {
        setIsMobileView(true);
      } else {
        setIsMobileView(false);
      }
    };

    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);
  const updateSelectedAgent = (agent: any) => {
    setSelectedAgent(agent);
  };
  const createAbortController = () => {
    const controller = new AbortController();
    setAbortController(controller);
    return controller;
  };

  // Abort all ongoing API requests
  const abortRequests = () => {
    if (abortController) {
      abortController.abort(new DOMException('Navigation aborted', 'AbortError'));
    }
    setAbortController(new AbortController()); // Reset with a new instance
  };

  type WizardStepData = {
    completed: boolean;
    data: Record<string, any>;
    error?: string;
  };

  type WizardDataType = {
    [step: string]: WizardStepData;
  };

  const personalities = {
    creativity: '50',
    decisiveness: '50',
    clarity: '50',
    confidence: '50',
    engagement: '50',
  };

  const initialWizardData: WizardDataType = {
    profile: {
      completed: false,
      data: {
        id: '',
        personality_traits: personalities,
        inference_configuration: {
          performance_config: {
            processing_mode: 'standard',
          },
        },
        name: '',
        objective: '',
      },
    },
    tasksAndTools: {
      completed: false,
      data: {
        tasks: [], tools: [], metahubGlossaries: [], knowledgeBases: [],
      },
    },
    guardrails: { completed: false, data: {} },
    others: { completed: false, data: {} },
    configuration: { completed: false, data: {} },
  };

  type ToolName = 'prompt' | 'api' | 'datahub' | 'integration';
  type PromptToolBasicInfo = {
    toolName: string;
    toolDescription: string;
  };

  type IntegrationToolBasicInfo = PromptToolBasicInfo & {
    environment?: [{}];
    atom?: string;
    process?: string;
  };

  type DatahubToolBasicInfo = PromptToolBasicInfo & {
    repository?: string;
    universe?: {};
    selectedRows?: [{}];
    field_operator?: '';
  };

  type ToolData = {
    id: '',
    toolBasicInfo: PromptToolBasicInfo | IntegrationToolBasicInfo | DatahubToolBasicInfo;
    inputParameters: Array<{
      inputName: string;
      inputDescription: string;
      inputType: string;
      required: boolean;
    }>;
    examples?: Array<{
      exampleInput: string;
      expectedOutput: string;
    }>;
    dynamicProcess?: Array<{
      inputValue: string;
      selectValue: string;
    }>;
    filterDatas?: Array<{
      fieldName: string;
      field_id: string;
      operator: string;
      values: any;
      date: any;
    }>;
    promptDescription?: '';
    configuration?: Array<{
      base_url: any;
      method: any;
      path: any;
      name: any;
      description: any;
      query_parameters: any[];
      path_parameters: any[];
      authentication: any;
      headers: any[];
      request_body: any;
    }>
    selectedRows?: [];
    filter_operator?: string;
  };

  const [wizardData, setWizardData] = useState<WizardDataType>(initialWizardData);
  const [toolWizardData, setToolWizardData] = useState<Record<ToolName, ToolData>>({
    prompt: {
      id: '',
      toolBasicInfo: { toolName: '', toolDescription: '' },
      inputParameters: [],
      promptDescription: '',
      examples: [],
    },
    api: {
      id: '',
      toolBasicInfo: { toolName: '', toolDescription: '' },
      inputParameters: [],
      configuration: [{
        base_url: '',
        path: '',
        name: '',
        description: '',
        method: '',
        query_parameters: [],
        path_parameters: [],
        authentication: '',
        headers: [],
        request_body: '',
      }],
    },
    datahub: {
      id: '',
      toolBasicInfo: {
        toolName: '', toolDescription: '', repository: '', universe: '',
      },
      inputParameters: [],
      filterDatas: [],
      selectedRows: [],
      filter_operator: '',
    },
    integration: {
      id: '',
      toolBasicInfo: {
        toolName: '',
        toolDescription: '',
        environment: [{
          id: '',
          name: '',
          classification: '',
          attached_atoms: [
            {
              id: '',
              name: '',
              status: '',
              type: '',
            },
          ],
        }],
        atom: '',
        process: '',
      },
      inputParameters: [],
      dynamicProcess: [],
    },
  });

  const clearToolWizardData = (tool: ToolName) => {
    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        id: '',
        toolBasicInfo: { toolName: '', toolDescription: '' },
        inputParameters: [],
        examples: [],
      },
    }));
  };
  const getDefaultToolWizardData = (type: string) => {
    if (type === 'api') {
      return {
        id: '',
        toolBasicInfo: { toolName: '', toolDescription: '' },
        inputParameters: [],
        configuration: [{
          base_url: '',
          path: '',
          name: '',
          description: '',
          method: '',
          query_parameters: [],
          path_parameters: [],
          authentication: '',
          headers: [],
          request_body: '',
        }],
      };
    }
    if (type === 'prompt') {
      return {
        id: '',
        toolBasicInfo: { toolName: '', toolDescription: '' },
        inputParameters: [],
        promptDescription: '',
        examples: [],
      };
    }
    if (type === 'integration') {
      return {
        id: '',
        toolBasicInfo: {
          toolName: '',
          toolDescription: '',
          environment: [{
            id: '',
            name: '',
            classification: '',
            attached_atoms: [
              {
                id: '',
                name: '',
                status: '',
                type: '',
              },
            ],
          }],
          atom: '',
          process: '',
        },
        inputParameters: [],
        dynamicProcess: [],
      };
    }
    if (type === 'datahub') {
      return {
        id: '',
        toolBasicInfo: {
          toolName: '', toolDescription: '', repository: '', universe: '',
        },
        inputParameters: [],
        filterDatas: [],
        selectedRows: [],
        filter_operator: '',
      };
    }
    return {};
  };
  const resetToolWizardData = () => {
    setToolWizardData({
      prompt: {
        id: '',
        toolBasicInfo: { toolName: '', toolDescription: '' },
        inputParameters: [],
        promptDescription: '',
        examples: [],
      },
      api: {
        id: '',
        toolBasicInfo: { toolName: '', toolDescription: '' },
        inputParameters: [],
        configuration: [{
          base_url: '',
          path: '',
          name: '',
          description: '',
          method: '',
          query_parameters: [],
          path_parameters: [],
          authentication: '',
          headers: [],
          request_body: '',
        }],
      },
      datahub: {
        id: '',
        toolBasicInfo: {
          toolName: '', toolDescription: '', repository: '', universe: '',
        },
        inputParameters: [],
        filterDatas: [],
        selectedRows: [],
        filter_operator: '',
      },
      integration: {
        id: '',
        toolBasicInfo: {
          toolName: '',
          toolDescription: '',
          environment: [{
            id: '',
            name: '',
            classification: '',
            attached_atoms: [
              {
                id: '',
                name: '',
                status: '',
                type: '',
              },
            ],
          }],
          atom: '',
          process: '',
        },
        inputParameters: [],
        dynamicProcess: [],
      },
    });
  };

  const updateToolBasicInfo = (tool: ToolName, field: string, value: string) => {
    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        toolBasicInfo: {
          ...prevData[tool].toolBasicInfo,
          [field]: value,
        },
      },
    }));
  };

  const addInputParameter = (tool: ToolName, newInput: {
    inputName: string;
    inputDescription: string;
    inputType: string;
    required: boolean;
  }) => {
    setToolWizardData((prevData) => {
      const updatedData = {
        ...prevData,
        [tool]: {
          ...prevData[tool],
          inputParameters: [...prevData[tool].inputParameters, newInput],
        },
      };
      return updatedData;
    });
  };

  const addExample = (tool: ToolName, newExample: {
    exampleInput: string;
    expectedOutput: string;
  }) => {
    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        examples: [...(prevData[tool].examples || []), newExample],
      },
    }));
  };

  const addDynamicProcess = (tool: ToolName) => {
    const dynamicProcess = toolWizardData[tool].dynamicProcess || [];
    const newDynamicProcess = [...dynamicProcess, { id: Date.now(), inputValue: '', selectValue: '' }];

    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        dynamicProcess: newDynamicProcess,
      },
    }));
  };

  const addFilterDatas = (tool: ToolName) => {
    const filterData = toolWizardData[tool].filterDatas || [];
    const newDynamicProcess = [...filterData, {
      id: Date.now(),
      fieldName: '',
      operator: '',
      values: [],
      time: '',
      date: '',
      repeatable: false,
      filter_operator: '',
    }];

    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        filterDatas: newDynamicProcess,
        filter_operator: newDynamicProcess.length === 2 ? 'AND'
          : prevData[tool].filter_operator,
      },
    }));
  };

  const handleDynamicProcessInputChange = (
    tool: ToolName,
    index: number,
    field: 'inputValue' | 'selectValue',
    value: string,
    type: string,
  ) => {
    const dynamicProcess = toolWizardData[tool].dynamicProcess || [];
    const newValue = { type, value };
    const updatedDynamicProcess = dynamicProcess.map(
      (process, i) => (i === index ? { ...process, [field]: newValue } : process),
    );

    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        dynamicProcess: updatedDynamicProcess,
      },
    }));
  };

  const handleFilterInputChange = (
    tool: ToolName,
    index: number,
    field: string,
    value: string,
    valueIndex: number,
  ) => {
    const filters = toolWizardData[tool].filterDatas || [];
    const newValue = { [field]: value };
    const updatedFilters = filters.map((filter, i) => {
      if (i === index) {
        // Ensure filter.values exists and is an array
        const updatedValues = [...filter.values]; // Clone the current values array
        // Initialize the sub-array at `valueIndex` if it doesn't exist
        updatedValues[valueIndex] = newValue;

        return {
          ...filter,
          values: updatedValues, // Update the `values` array with the modified values
        };
      }
      return filter; // Leave other filters unchanged
    });

    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        filterDatas: updatedFilters,
      },
    }));
  };

  const deleteDynamicProcess = (tool: ToolName, index: number) => {
    const dynamicProcess = toolWizardData[tool].dynamicProcess || [];
    const updatedDynamicProcess = dynamicProcess.filter((_, i) => i !== index);

    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        dynamicProcess: updatedDynamicProcess,
      },
    }));
  };

  const deleteFilters = (tool: ToolName, index: number) => {
    const filterData = toolWizardData[tool].filterDatas || [];
    const updatedFilterData = filterData.filter((_, i) => i !== index);

    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        filterDatas: updatedFilterData,
        filter_operator: updatedFilterData.length === 1 ? '' : prevData[tool].filter_operator,
      },
    }));
  };
  function getRequestType(type: any) {
    switch (type) {
      case 'JSON':
        return 'application/json';

      case 'XML':
        return 'application/xml';

      case 'FORM':
        return 'application/x-www-form-urlencoded';

      case 'MULTIPART':
        return 'multipart/form-data';

      case 'TEXT':
        return 'text/plain';

      default:
        return 'none';
    }
  }
  const initializeConfiguration = (prevData: any) => {
    if (!prevData.api.configuration) {
      return [{
        endpointUrl: '',
        endpointPath: '',
        queryParameters: [],
        pathParameters: [],
        authentication: null,
        headers: [],
        request_body: null,
        config: {},
      }];
    }
    return [...prevData.api.configuration];
  };
  const handleAuthenticationUpdate = (config: any, value: any) => ({
    ...config,
    authentication: {
      ...config.authentication,
      ...value,
    },
  });
  const handleArrayFieldUpdate = (config: any, field: string, value: any, index?: number) => {
    if (!Array.isArray(config[field])) {
      config[field] = [];
    }
    if (typeof index === 'number') {
      config[field][index] = value;
    } else {
      config[field] = value;
    }
    return config;
  };
  const handleRequestBodyUpdate = (config: any, field: string, value: any) => {
    if (!config.request_body) {
      config.request_body = {};
    }
    if (field === 'request_body') {
      config.request_body.template = value;
    } else if (field === 'request_body_type') {
      config.request_body.type = getRequestType(value);
    }
    return config;
  };

  const updateApiConfigurationField = (
    field: string,
    value: any,
    index?: number,
  ) => {
    setToolWizardData((prevData: any) => {
      const updatedConfigurations = initializeConfiguration(prevData);

      if (field === 'authentication') {
        updatedConfigurations[0] = handleAuthenticationUpdate(updatedConfigurations[0], value);
      } else if (
        field === 'pathParameters'
        || field === 'path_parameters'
        || field === 'queryParameters'
        || field === 'query_parameters'
        || field === 'headers'
      ) {
        updatedConfigurations[0] = handleArrayFieldUpdate(
          updatedConfigurations[0],
          field,
          value,
          index,
        );
      } else if (['request_body', 'request_body_type'].includes(field)) {
        updatedConfigurations[0] = handleRequestBodyUpdate(updatedConfigurations[0], field, value);
      } else {
        updatedConfigurations[0][field] = value;
      }
      return {
        ...prevData,
        api: {
          ...prevData.api,
          configuration: updatedConfigurations,
        },
      };
    });
  };

  const resetWizardData = () => {
    setWizardData(initialWizardData);
  };

  const clearStepData = (step: string) => {
    setWizardData((prevData: any) => ({
      ...prevData,
      [step]: { data: {} },
    }));
  };

  const updateWizardStep = (
    step: string,
    newData: Partial<Record<string, any>> | ((prevStepData: any) => Partial<Record<string, any>>),
  ) => {
    setWizardData((prevData) => ({
      ...prevData,
      [step]: {
        ...prevData[step],
        data: typeof newData === 'function'
          ? newData(prevData[step]?.data)
          : { ...prevData[step].data, ...newData },
      },
    }));
  };

  const updateToolWizardData = (tool: ToolName, newData: Partial<Record<string, any>>) => {
    setToolWizardData((prevData) => ({
      ...prevData,
      [tool]: {
        ...prevData[tool],
        ...newData,
      },
    }));
  };

  const completeStep = (step: string) => {
    setWizardData((prevData) => ({
      ...prevData,
      [step]: { ...prevData[step], completed: true },
    }));
  };

  const value = useMemo(
    () => ({
      selectedAgent,
      setSelectedAgent,
      builderDialogTitle,
      setBuilderDialogTitle,
      customAgentChatSessionId,
      setCustomAgentChatSessionId,
      showBuilderDialog,
      setShowBuilderDialog,
      isLeftNavCollapsed,
      setIsLeftNavCollapsed,
      isFloatingPanelPinned,
      setIsFloatingPanelPinned,
      showPreview,
      setShowPreview,
      isConversationInProgress,
      setIsConversationInProgress,
      showCustomAgentConversationPage,
      setShowCustomAgentConversationPage,
      showAddNewTaskDialog,
      setShowAddNewTaskDialog,
      showDialogIcon,
      setDialogIcon,
      testMessages,
      setTestMessages,
      customAgentChatMessages,
      setCustomAgentChatMessages,
      addNewTaskDialogTitle,
      setAddNewTaskDialogTitle,
      isMobileView,
      setIsMobileView,
      initialCustomAgentUserPrompt,
      setInitialCustomAgentUserPrompt,
      initialCustomAgentUserFiles,
      setInitialCustomAgentUserFiles,
      showAIGeneratedInstructions,
      setShowAIGeneratedInstructions,
      showToolsSelectionTable,
      setShowToolsSelectionTable,
      wizardData,
      setWizardData,
      resetWizardData,
      updateWizardStep,
      completeStep,
      clearStepData,
      toolWizardData,
      setToolWizardData,
      updateToolWizardData,
      clearToolWizardData,
      resetToolWizardData,
      getDefaultToolWizardData,
      updateToolBasicInfo,
      addInputParameter,
      addExample,
      addDynamicProcess,
      handleDynamicProcessInputChange,
      deleteDynamicProcess,
      updateSelectedAgent,
      addFilterDatas,
      updateApiConfigurationField,
      handleFilterInputChange,
      deleteFilters,
      isReffererTool,
      setIsReffererTool,
      cachedSourceDetails,
      setCachedSourceDetails,
      cachedBulkToolsData,
      setCachedBulkToolsData,
      cachedBulkKnowledgeBasesData,
      setCachedBulkKnowledgeBasesData,
      cachedBulkGlossariesData,
      setCachedBulkGlossariesData,
      userPrivileges,
      setUserPrivileges,
      userResponse,
      setUserResponse,
      abortController,
      createAbortController,
      abortRequests,
      createAgentMethod,
      setCreateAgentMethod,
      currentView,
      setCurrentView,
      integrationRecommendationIcon,
      setIntegrationRecommendationIcon,
      savedFileInferenceConfig,
      setSavedFileInferenceConfig,
      savedConversationStarters,
      setSavedConversationStarters,
      boomiModels,
      setBoomiModels,
    }),
    // eslint-disable-next-line
    [
      builderDialogTitle,
      showBuilderDialog,
      customAgentChatSessionId,
      setCustomAgentChatSessionId,
      showPreview,
      isConversationInProgress,
      showCustomAgentConversationPage,
      showAddNewTaskDialog,
      showDialogIcon,
      addNewTaskDialogTitle,
      isMobileView,
      initialCustomAgentUserPrompt,
      initialCustomAgentUserFiles,
      showAIGeneratedInstructions,
      showToolsSelectionTable,
      wizardData,
      toolWizardData,
      selectedAgent,
      isLeftNavCollapsed,
      isFloatingPanelPinned,
      testMessages,
      customAgentChatMessages,
      isReffererTool,
      cachedSourceDetails,
      cachedBulkToolsData,
      cachedBulkKnowledgeBasesData,
      cachedBulkGlossariesData,
      userPrivileges,
      userResponse,
      abortController,
      createAgentMethod,
      currentView,
      integrationRecommendationIcon,
      savedFileInferenceConfig,
      savedConversationStarters,
      boomiModels,
    ],
  );

  return (
    <BoomiAgentFoundationContext.Provider value={value}>
      {children}
    </BoomiAgentFoundationContext.Provider>
  );
}

export const useBoomiAgentFoundationContext = () => {
  const context = useContext(BoomiAgentFoundationContext);
  if (!context) {
    throw new Error('useMyContext must be used within a MyContextProvider');
  }
  return context;
};

export default BoomiAgentFoundationContextProvider;
